// boxFields.ts — the Segmentation card's box editor state. The six edges are
// kept as the strings the user typed (so '', '-' and '-3' are legal
// intermediate states of a number input: a numeric state would coerce '' to
// 0 and React would write '0' back into the field), and parsed only when the
// box is sent. Values are world mm in the UI and metres on the wire.

import type { BoxM } from '../api';

export interface BoxFields {
  min: string[];
  max: string[];
}

export const EMPTY_BOX_FIELDS: BoxFields = { min: ['0', '0', '0'], max: ['0', '0', '0'] };

// boxFieldsFromMetres seeds the editor from a box in metres, rounded to mm.
export function boxFieldsFromMetres(min: readonly number[], max: readonly number[]): BoxFields {
  const mm = (v: number) => String(Math.round(v * 1000));
  return { min: min.map(mm), max: max.map(mm) };
}

// parseEdge parses one field; blank or partial input ('', '-', '.') is invalid.
export function parseEdge(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// parseBoxFields returns the box in metres, or null while any field is not a
// finite number or a min edge exceeds its max.
export function parseBoxFields(f: BoxFields): BoxM | null {
  const min: number[] = [];
  const max: number[] = [];
  for (let k = 0; k < 3; k++) {
    const lo = parseEdge(f.min[k] ?? '');
    const hi = parseEdge(f.max[k] ?? '');
    if (lo === null || hi === null || lo > hi) return null;
    min.push(lo / 1000);
    max.push(hi / 1000);
  }
  return { min: min as [number, number, number], max: max as [number, number, number] };
}

// setBoxField returns `f` with one edge replaced by the raw input string.
export function setBoxField(f: BoxFields, edge: 'min' | 'max', k: number, raw: string): BoxFields {
  return { ...f, [edge]: f[edge].map((x, i) => (i === k ? raw : x)) };
}
